import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ResponseModel } from '@dental-ui/account/models';
import { buildApiUrl, buildRequestHeaders } from '@dental-ui/shared/consts';
import {
  PortalAdminListRequest,
  PortalAdminListResult,
  PortalAdminResource,
} from '@dental-ui/portal-dashboard';

interface ApiPaginationResponse {
  page?: number;
  Page?: number;
  numberOfRecord?: number;
  NumberOfRecord?: number;
  totalPage?: number;
  TotalPage?: number;
  totalRecordCount?: number;
  TotalRecordCount?: number;
  totalCount?: number;
  TotalCount?: number;
  data?: unknown[];
  Data?: unknown[];
}

@Injectable({
  providedIn: 'root',
})
export class PortalResourceApiService {
  private readonly http = inject(HttpClient);

  loadResource(
    resource: PortalAdminResource,
    request: PortalAdminListRequest
  ): Observable<PortalAdminListResult> {
    if (resource.method === 'GET') {
      return this.http
        .get<ResponseModel<unknown[] | ApiPaginationResponse>>(buildApiUrl(resource.endpoint), {
          params: this.buildQueryParams(request),
          headers: buildRequestHeaders(true),
        })
        .pipe(map((response) => this.normalizeResponse(response, request)));
    }

    return this.http
      .post<ResponseModel<unknown[] | ApiPaginationResponse>>(
        buildApiUrl(resource.endpoint),
        this.buildFilterBody(resource, request),
        {
          headers: buildRequestHeaders(true),
        }
      )
      .pipe(map((response) => this.normalizeResponse(response, request)));
  }

  private buildQueryParams(request: PortalAdminListRequest): HttpParams {
    let params = new HttpParams()
      .set('Page', String(request.page + 1))
      .set('NumberOfRecord', String(request.pageSize));

    if (request.searchTerm) {
      params = params.set('SearchText', request.searchTerm);
    }

    return params;
  }

  private buildFilterBody(
    resource: PortalAdminResource,
    request: PortalAdminListRequest
  ): Record<string, unknown> {
    const filter: Record<string, unknown> = {};

    if (resource.searchField && request.searchTerm) {
      filter[resource.searchField] = request.searchTerm;
    }

    return {
      PaginationRequestModel: {
        Page: request.page + 1,
        NumberOfRecord: request.pageSize,
        SortColumn: 'ID',
        SortOrder: 'desc',
      },
      ...(resource.filterBodyKey ? { [resource.filterBodyKey]: filter } : {}),
    };
  }

  private normalizeResponse(
    response: ResponseModel<unknown[] | ApiPaginationResponse>,
    request: PortalAdminListRequest
  ): PortalAdminListResult {
    const payload = response.data;
    const pageData = Array.isArray(payload) ? undefined : payload;
    const rawRows = Array.isArray(payload) ? payload : pageData?.data ?? pageData?.Data ?? [];
    const data = rawRows.filter(this.isRecord);
    const totalPages = pageData?.totalPage ?? pageData?.TotalPage ?? (data.length ? 1 : 0);
    const totalItems =
      pageData?.totalRecordCount ??
      pageData?.TotalRecordCount ??
      pageData?.totalCount ??
      pageData?.TotalCount ??
      totalPages * request.pageSize;

    return {
      data,
      page: Math.max((pageData?.page ?? pageData?.Page ?? request.page + 1) - 1, 0),
      pageSize: pageData?.numberOfRecord ?? pageData?.NumberOfRecord ?? request.pageSize,
      totalPages,
      totalItems,
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
